/**
 * Chunked reader: streams a sheet's used-range subset through a sink
 * callback in adaptively-sized chunks. The single bridge call per chunk
 * loads `values`, `formulasR1C1`, and `numberFormat`; the chunk is fed
 * synchronously to the consumer and then dropped before the next sync.
 *
 * Per the architecture: never hold a range proxy or its JS arrays after
 * a sync. The walk-and-release pattern keeps peak memory at one chunk
 * worth of bridge marshalling regardless of sheet size.
 *
 * Chunk sizes start at a platform-derived budget and halve on payload
 * errors (adaptive backoff). After 8 halvings or hitting height 1 with
 * a still-failing sync, the reader rethrows the last error — at that
 * point even a single row is too wide for the bridge.
 *
 * Why a callback rather than an async iterator: Office.js holds the
 * marshalled JS arrays inside the range proxy. The callback model lets
 * us walk-and-release synchronously inside one Excel.run lifetime; an
 * async iterator would require the caller to await between yields,
 * during which the chunk's arrays stay live, defeating the streaming
 * intent.
 */

import type { IngestionTarget } from "./catalogs";

export interface SheetChunk {
  /**
   * Cell values as Office.js marshalled them. Each cell may be number,
   * string, boolean, null, or — for error cells — the error literal as
   * a string. The sink narrows per cell via `typeof`.
   */
  values: unknown[][];
  /**
   * R1C1 form for formula cells; for non-formula cells, the value
   * itself (typed identically to `values`). The sink keys off the
   * `=`-prefixed string form to identify formulas.
   */
  formulasR1C1: unknown[][];
  /**
   * A1 form for formula cells; same shape as formulasR1C1 — non-formula
   * cells get the value itself. Loaded alongside R1C1 because some
   * legacy analyzers parse A1 syntax directly.
   */
  formulas: unknown[][];
  /**
   * Number format codes (e.g. "0.00", "m/d/yyyy", "General"). The sink
   * keys off these to distinguish DATE from NUMBER value-kinds.
   */
  numberFormat: unknown[][];
  /** Row index within the bounds: 0 = first row of bounds. */
  rowOffset: number;
  /** Height of this chunk in rows. */
  rows: number;
  /** Width — same as the target's bounds.cols. */
  cols: number;
}

export type ChunkHandler = (chunk: SheetChunk) => void;

/** Maximum halvings before giving up. Each halving cuts chunk height in half. */
const MAX_BACKOFFS = 8;

export interface ChunkBudget {
  /** Initial chunk height in rows, given the column count. */
  initialHeight(cols: number): number;
}

/**
 * Default chunk budget. Web (OfficeOnline) hits the 5MB bridge payload
 * cap before the cell-count cap, so we use ~18k cells per chunk
 * (empirically safe). Desktop / Mac get a much larger budget that
 * rides closer to the 5M-cells-per-range hard limit.
 *
 * Wrapped in try/catch because Office may not be initialized when this
 * is first evaluated (e.g. in tests). Falls back to the conservative
 * web budget when uncertain.
 */
export function defaultChunkBudget(): ChunkBudget {
  let isOfficeOnline = true; // conservative default
  try {
    const p =
      (window as { Office?: typeof Office }).Office?.context?.platform;
    if (p !== undefined && p !== null) {
      isOfficeOnline = p === Office.PlatformType.OfficeOnline;
    }
  } catch {
    // Office not available; stick with conservative default.
  }

  if (isOfficeOnline) {
    // ~12k cells per chunk now that we load both R1C1 and A1 formulas
    // alongside values + numberFormat. The dual formula load roughly
    // doubles per-cell bridge payload on formula-heavy sheets, so we
    // dropped from 18k to 12k to keep clear of the 5MB cap and avoid
    // the backoff loop on first-chunk failures.
    return {
      initialHeight: (cols: number) =>
        Math.max(1, Math.floor(12000 / Math.max(1, cols))),
    };
  }
  return {
    initialHeight: (cols: number) =>
      Math.min(50000, Math.max(1, Math.floor(5_000_000 / Math.max(1, cols)))),
  };
}

/**
 * Stream the target's range in adaptive chunks. The `onChunk` callback
 * runs synchronously inside the loop — it must finish before the next
 * sync so the previous chunk's marshalled arrays can be released.
 *
 * On any sync error, the reader halves the chunk height and retries the
 * same `rowOffset`. After 8 halvings or hitting height 1 the last error
 * is rethrown with context (sheet name, row, height).
 *
 * Empty bounds (`rows === 0` or `cols === 0`) return immediately
 * without making any Office.js calls.
 */
export async function readSheetInChunks(
  target: IngestionTarget,
  onChunk: ChunkHandler,
  budget: ChunkBudget = defaultChunkBudget()
): Promise<void> {
  const { bounds, name: sheetName } = target;
  if (bounds.rows === 0 || bounds.cols === 0) return;

  await Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItem(sheetName);
    let chunkHeight = budget.initialHeight(bounds.cols);
    let backoffs = 0;
    let rowOffset = 0;

    while (rowOffset < bounds.rows) {
      const thisHeight = Math.min(chunkHeight, bounds.rows - rowOffset);
      const range = sheet.getRangeByIndexes(
        bounds.startRow + rowOffset,
        bounds.startCol,
        thisHeight,
        bounds.cols
      );
      range.load(["values", "formulasR1C1", "formulas", "numberFormat"]);

      try {
        // eslint-disable-next-line office-addins/no-context-sync-in-loop
        await ctx.sync();
      } catch (err) {
        // Most likely: payload too large. Halve and retry, unless we've
        // already halved enough times or are at the minimum height.
        if (thisHeight <= 1 || backoffs >= MAX_BACKOFFS) {
          throw new Error(
            `chunked read failed on ${sheetName} at absolute row ${
              bounds.startRow + rowOffset
            }, chunk height ${thisHeight}, after ${backoffs} backoff(s): ${describeError(
              err
            )}`
          );
        }
        chunkHeight = Math.max(1, Math.floor(chunkHeight / 2));
        backoffs++;
        continue; // retry same rowOffset with smaller chunk
      }

      onChunk({
        values: range.values as unknown[][],
        formulasR1C1: range.formulasR1C1 as unknown[][],
        formulas: range.formulas as unknown[][],
        numberFormat: range.numberFormat as unknown[][],
        rowOffset,
        rows: thisHeight,
        cols: bounds.cols,
      });

      rowOffset += thisHeight;
    }
  });
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
